import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Shield, Sparkles, UserSearch, Lock, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  pro: {
    slug: "pro",
    label: "Pflege-KI Kompakt",
    fullLabel: "Dickies-Campus · Pflege-KI Kompakt",
    tagline: "Für Pflegedienste bis 10 Mitarbeiter — digitale Patientenmappe, QPR-26 & Team-Chat komplett dabei.",
    priceLine: "199 € / Monat",
    priceFootnote: "zzgl. MwSt. · monatlich kündbar · Beispiel-Mappe inklusive",
    audienceTag: "Bis 10 Mitarbeiter · 1 PDL + 10 Sub-Accounts",
    accentClass: "text-terra",
    features: [
      "Digitale Patientenmappe · 11 Sektionen (Stammdaten · SIS · Risikomatrix · Medikation · Maßnahmenplan · Versorgungsauftrag · 360°-Beobachtung · Vitalwerte · QPR-26 · Freigaben · Team-Chat)",
      "QPR-26 Prüfbogen · alle 26 Kriterien in 7 Prüfbereichen · Original-Wortlaut GKV",
      "SIS-Sprachdiktat mit Whisper direkt in jede Textarea",
      "Full-PDF-Export der Mappe · Cover, 11 Sektionen, Signaturen als Bild-Embed",
      "Team-Chat pro Mappe · PDL & Mitarbeiter stimmen sich pro Bewohner ab",
      "Sammel-Audit-CSV · alle Patientenmappen mit QPR-Status als Excel",
      "Digitale Freigabe mit Signatur-Pad · rechtssichere PDL-Unterschrift",
      "Halluzinationsschutz aktiv · MDK-Schutzschild Premium · Prio-Support",
      "1 PDL + bis zu 10 Mitarbeiter-Sub-Accounts · Team-Datenscoping automatisch",
      "PWA-App · installierbar auf iOS/Android · signature DH-Icon",
    ],
    notIncluded: ["Ab 11. Mitarbeiter → Pflege-KI Team"],
    extraSection: null,
  },
  team: {
    slug: "team",
    label: "Pflege-KI Team",
    fullLabel: "Dickies-Campus · Pflege-KI Team",
    tagline: "Gleicher Funktionsumfang wie Kompakt — nur ohne Team-Limit. Für Pflegedienste ab 11 Mitarbeiter.",
    priceLine: "349 € / Monat",
    priceFootnote: "zzgl. MwSt. · monatlich kündbar · Onboarding-Call inklusive",
    audienceTag: "Ab 11 Mitarbeiter · unbegrenzt Sub-Accounts",
    accentClass: "text-terra",
    features: [
      "Alle Kompakt-Features · uneingeschränkt",
      "Digitale Patientenmappe · 11 Sektionen inkl. QPR-26 & Team-Chat",
      "SIS-Sprachdiktat mit Whisper · Full-PDF-Export · Signatur-Freigabe",
      "Sammel-Audit-CSV · Halluzinationsschutz · MDK-Schutzschild Premium",
      "Unbegrenzt Mitarbeiter-Sub-Accounts · keine 10-Personen-Grenze",
      "1:1 Onboarding-Call für PDL & Kernteam inklusive",
      "Jahres-Pflichtfortbildungen für PDL + Stellvertreter (24h PA · 16h Betreuung)",
      "Vertraglich vereinbarter Abwerbeschutz für dein gesamtes Team",
    ],
    notIncluded: [],
    extraSection: "b2b-xl-vorteil",
  },
};

export default function CampusTierLanding({ tier }) {
  const cfg = TIER_CONFIG[tier];
  const navigate = useNavigate();
  const { user, ready } = useCampusAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    document.title = `${cfg.label} · ${cfg.priceLine} | Dickies-Campus`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = `${cfg.label} – ${cfg.tagline} ${cfg.priceLine}, monatlich kündbar. MDK-Schutzschild, Schatzkiste, Live-Briefings.`;
  }, [cfg]);

  if (!cfg) return null;

  const handleCheckout = async () => {
    setErr(null);
    if (!ready) return;
    // Admin braucht kein Abo – sie haben bereits alle Tiers freigeschaltet.
    if (user?.is_admin) {
      navigate("/campus/app/pflege-ki");
      return;
    }
    // Hauptquartier (Team) braucht persönliche Beratung – KEIN Self-Service-Stripe.
    if (tier === "team") {
      navigate("/campus#anfrage");
      setTimeout(() => {
        const el = document.getElementById("anfrage");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return;
    }
    if (!user) {
      navigate("/campus/login", { state: { from: { pathname: `/campus/${tier}` } } });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/checkout`,
        { tier, origin_url: window.location.origin },
        { withCredentials: true, timeout: 25000 },
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setErr("Checkout konnte nicht gestartet werden.");
    } catch (e) {
      // Cloudflare 5xx liefert HTML – nicht roh anzeigen.
      const respData = e?.response?.data;
      let detail = null;
      if (respData && typeof respData === "object" && respData.detail) {
        detail = formatApiErrorDetail(respData.detail);
      } else if (typeof respData === "string" && respData.length < 200 && !respData.includes("<")) {
        detail = respData;
      }
      if (e?.code === "ECONNABORTED") {
        setErr("Zahlungsanbieter antwortet gerade nicht. Bitte in einem Moment erneut versuchen.");
      } else {
        setErr(detail || "Checkout vorübergehend nicht erreichbar. Bitte in einem Moment erneut versuchen.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />

      <main className="pt-24 md:pt-32">
        {/* HERO */}
        <section className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-14 lg:gap-20 items-start">
            <div>
              <div className="inline-flex items-start gap-2 text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] font-semibold text-terra mb-6 max-w-full">
                <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={2} />
                <span className="leading-tight">Dickies-Campus · {cfg.audienceTag}</span>
              </div>
              <h1 className="font-display text-sand-800 tracking-tight leading-[1.04] text-[2.6rem] md:text-[3.4rem] lg:text-[4rem] font-semibold">
                {cfg.label}.
                <br />
                <span className={cfg.accentClass}>Der ruhige Vorsprung.</span>
              </h1>
              <p className="mt-6 text-[17px] md:text-[18.5px] leading-[1.6] text-sand-600 max-w-xl">
                {cfg.tagline} MDK-Soforthilfe, geprüfte Vorlagen, monatliche Live-Briefings —
                ohne SaaS-Lärm, ohne überteuerte Berater.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button
                  onClick={handleCheckout}
                  disabled={busy || !ready}
                  data-testid={`tier-${tier}-cta-hero`}
                  className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-13 px-7 text-[14.5px] font-medium disabled:opacity-60"
                >
                  <span className="inline-flex items-center gap-2">
                    {busy
                      ? "Wird vorbereitet…"
                      : user?.is_admin
                        ? "Direkt zur Pflege-KI →"
                        : (tier === "team" ? "Pflege-KI Team starten" : "Pflege-KI Kompakt starten")}
                    {!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
                  </span>
                </Button>
                <div className="text-[13px] text-sand-500 leading-tight">
                  <span className="text-sand-800 font-semibold">{cfg.priceLine}</span>
                  <span className="mx-2">·</span>
                  {cfg.priceFootnote}
                </div>
              </div>
              {user?.is_admin && (
                <div
                  data-testid={`tier-${tier}-admin-notice`}
                  className="mt-5 rounded-2xl border border-sage-dark/40 bg-sage-dark/5 px-4 py-3 text-[13px] text-sand-700 max-w-xl"
                >
                  <strong className="text-sage-dark">Du bist Admin</strong> – alle Tiers
                  inklusive Pflege-KI sind für dich bereits freigeschaltet. Kein Stripe-Abo nötig.
                </div>
              )}
              {err && (
                <p className="mt-4 text-[13px] text-terra font-medium" data-testid={`tier-${tier}-error`}>
                  {err}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-sand-500">
                <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" strokeWidth={1.8} /> Stripe-gesichert</span>
                <span>·</span>
                <span>Monatlich kündbar</span>
                <span>·</span>
                <span>Made in Germany</span>
              </div>
            </div>

            {/* Right: feature list card – Apple Education vibe */}
            <aside className="lg:sticky lg:top-28">
              <div className="rounded-3xl bg-white border border-cream-300 p-8 shadow-soft">
                <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra mb-4">
                  Enthalten
                </div>
                <ul className="grid gap-3.5">
                  {cfg.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[14.5px] text-sand-700 leading-snug">
                      <Check className="h-5 w-5 text-terra flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {cfg.notIncluded.length > 0 && (
                  <div className="mt-7 pt-6 border-t border-cream-300">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-sand-400 mb-2">
                      Nicht enthalten
                    </div>
                    <ul className="text-[13px] text-sand-500 space-y-1">
                      {cfg.notIncluded.map((f) => (<li key={f}>· {f}</li>))}
                    </ul>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>

        {/* PROOF / TRUST */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <div className="font-display text-[2.4rem] text-sand-800 font-semibold">2.300+</div>
              <div className="text-[13px] text-sand-500 uppercase tracking-[0.18em] font-semibold mt-1">Pflegekräfte ausgebildet</div>
            </div>
            <div>
              <div className="font-display text-[2.4rem] text-sand-800 font-semibold">120+</div>
              <div className="text-[13px] text-sand-500 uppercase tracking-[0.18em] font-semibold mt-1">Pflegedienste betreut</div>
            </div>
            <div>
              <div className="font-display text-[2.4rem] text-sand-800 font-semibold">4,9/5</div>
              <div className="text-[13px] text-sand-500 uppercase tracking-[0.18em] font-semibold mt-1">Mitglieder-Bewertung</div>
            </div>
          </div>
        </section>

        {/* XL-Vorteil – nur für Hauptquartier */}
        {cfg.extraSection === "b2b-xl-vorteil" && (
          <section className="max-w-6xl mx-auto px-6 md:px-10 mt-24 md:mt-28">
            <div className="rounded-3xl border border-terra/20 bg-gradient-to-br from-peach-50 to-cream-50 p-8 md:p-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terra text-cream-50 text-[11px] uppercase tracking-[0.22em] font-bold">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                XL-Vorteil
              </div>
              <h2 className="mt-6 font-display text-[2rem] md:text-[2.4rem] text-sand-800 font-semibold leading-tight">
                Ihre Jahrespflichtfortbildungen sind <span className="text-terra">schon bezahlt</span>.
              </h2>
              <p className="mt-6 text-sand-700 text-[16px] leading-relaxed max-w-3xl">
                Rechnen Sie knallhart nach: Bei herkömmlichen Anbietern zahlen Sie pro Mitarbeiter
                und Jahr schnell <strong className="text-sand-800">250 € bis 300 €</strong> für die
                gesetzlichen Pflichtnachweise. Im Helden-Hauptquartier ist die jährliche
                Pflichtfortbildung für <strong className="text-sand-800">bis zu 5 Ihrer
                angemeldeten Führungskräfte</strong> komplett inklusive — die 24h-Fortbildung
                für Praxisanleiter ebenso wie die 16h-Fortbildung für Betreuungskräfte.
              </p>
              <p className="mt-4 text-sand-700 text-[16px] leading-relaxed max-w-3xl">
                Sie schlagen <strong className="text-terra">drei Fliegen mit einer Klappe</strong>:
                Sie sichern Ihre Führungskräfte rechtlich ab, finden neue Mitarbeiter
                (heiß begehrte LG1- und LG2-Kräfte) direkt aus unseren Absolventen-Pools
                und sparen jedes Jahr hunderte Euro an externen Fortbildungskosten.
              </p>
              <div className="mt-9 grid sm:grid-cols-3 gap-4">
                <div className="rounded-2xl bg-white border border-cream-300 p-5">
                  <UserSearch className="h-5 w-5 text-terra" strokeWidth={1.7} />
                  <div className="mt-3 text-[14px] font-semibold text-sand-800">Recruiting inklusive</div>
                  <div className="mt-1 text-[13px] text-sand-600 leading-snug">Direkt-Pipeline zu frischen LG1/LG2-Absolventen</div>
                </div>
                <div className="rounded-2xl bg-white border border-cream-300 p-5">
                  <Lock className="h-5 w-5 text-terra" strokeWidth={1.7} />
                  <div className="mt-3 text-[14px] font-semibold text-sand-800">Abwerbeschutz für alle</div>
                  <div className="mt-1 text-[13px] text-sand-600 leading-snug">Gilt im gesamten Campus — auch in den Free-Tarifen</div>
                </div>
                <div className="rounded-2xl bg-white border border-cream-300 p-5">
                  <Award className="h-5 w-5 text-terra" strokeWidth={1.7} />
                  <div className="mt-3 text-[14px] font-semibold text-sand-800">Pflicht-Fobi gratis</div>
                  <div className="mt-1 text-[13px] text-sand-600 leading-snug">Spart 250–300 € / Mitarbeiter / Jahr</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="max-w-3xl mx-auto px-6 md:px-10 mt-28 mb-28 text-center">
          <h2 className="font-display text-sand-800 tracking-tight text-[2rem] md:text-[2.4rem] leading-[1.15] font-semibold">
            Heute beitreten.<br />
            <span className="text-terra">Morgen ruhiger arbeiten.</span>
          </h2>
          <p className="mt-5 text-sand-600 text-[15.5px] leading-relaxed">
            {cfg.priceLine} · monatlich kündbar · sofortiger Zugang nach Zahlung.
          </p>
          <Button
            onClick={handleCheckout}
            disabled={busy || !ready}
            data-testid={`tier-${tier}-cta-final`}
            className="mt-10 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-13 px-8 text-[14.5px] font-medium disabled:opacity-60"
          >
            <span className="inline-flex items-center gap-2">
              {busy ? "Wird vorbereitet…" : (tier === "team" ? "Pflege-KI Team starten" : "Pflege-KI Kompakt starten")}
              {!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
            </span>
          </Button>
          <p className="mt-6 text-[12px] text-sand-500">
            Schon Mitglied?{" "}
            <Link to="/campus/login" className="underline-offset-4 hover:underline">Anmelden</Link>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
